import { AxiosRequestConfig, Method } from 'axios'

import { axiosInstance } from '../api_helper'

export type AccountingAccessProvider = 'quickbooks' | 'xero'

export type AccountingAccessCredentials =
  | {
      kind: 'signed'
      userId: string
      sessionId: string
      exp: string
      sig: string
    }
  | { kind: 'legacyKey'; key: string }

export type AccountingAccess = {
  merchantId: string
  credentials: AccountingAccessCredentials
}

export type AccountingAccessErrorCode =
  | 'link_invalid'
  | 'link_expired'
  | 'access_denied'

const SIGNED_PARAMS = ['userId', 'sessionId', 'exp', 'sig'] as const

const LEGACY_LOCAL_STORAGE_KEYS = ['APIKey', 'merchantId']

function storageKey(provider: AccountingAccessProvider): string {
  return `accountingAccess:${provider}`
}

export function readSignedCredentials(
  query: URLSearchParams,
): AccountingAccessCredentials | undefined {
  const values = SIGNED_PARAMS.map((name) => query.get(name))

  if (values.some((value) => !value)) {
    return undefined
  }

  const [userId, sessionId, exp, sig] = values as string[]

  return { kind: 'signed', userId, sessionId, exp, sig }
}

export function storeAccess(
  provider: AccountingAccessProvider,
  access: AccountingAccess,
): void {
  try {
    sessionStorage.setItem(storageKey(provider), JSON.stringify(access))
  } catch {
    return
  }
}

export function readStoredAccess(
  provider: AccountingAccessProvider,
): AccountingAccess | undefined {
  try {
    const stored = sessionStorage.getItem(storageKey(provider))

    return stored ? (JSON.parse(stored) as AccountingAccess) : undefined
  } catch {
    return undefined
  }
}

export function clearStoredAccess(provider: AccountingAccessProvider): void {
  try {
    sessionStorage.removeItem(storageKey(provider))
  } catch {
    return
  }
}

export function clearLegacyStorage(): void {
  try {
    LEGACY_LOCAL_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key))
  } catch {
    return
  }
}

export function resolveAccess({
  provider,
  merchantId,
  query,
  legacyKey,
}: {
  provider: AccountingAccessProvider
  merchantId: string | null | undefined
  query: URLSearchParams
  legacyKey?: string | null
}): AccountingAccess | undefined {
  clearLegacyStorage()

  const signed = readSignedCredentials(query)

  if (merchantId && signed) {
    const access = { merchantId, credentials: signed }
    storeAccess(provider, access)

    return access
  }

  if (merchantId && legacyKey) {
    const access: AccountingAccess = {
      merchantId,
      credentials: { kind: 'legacyKey', key: legacyKey },
    }
    storeAccess(provider, access)

    return access
  }

  const stored = readStoredAccess(provider)

  if (stored && (!merchantId || stored.merchantId === merchantId)) {
    return stored
  }

  return undefined
}

export function requestConfigFor(
  credentials: AccountingAccessCredentials,
  params: Record<string, unknown> = {},
  headers: Record<string, string> = {},
): AxiosRequestConfig {
  if (credentials.kind === 'signed') {
    const { userId, sessionId, exp, sig } = credentials

    return { params: { ...params, userId, sessionId, exp, sig }, headers }
  }

  return { params, headers: { ...headers, 'x-api-key': credentials.key } }
}

export function appendCredentials(
  url: URL,
  credentials: AccountingAccessCredentials,
): URL {
  if (credentials.kind === 'signed') {
    SIGNED_PARAMS.forEach((name) =>
      url.searchParams.set(name, credentials[name]),
    )
  } else {
    url.searchParams.set('key', credentials.key)
  }

  return url
}

export function accountingAccessErrorCode(
  error: unknown,
): AccountingAccessErrorCode | undefined {
  const status = (error as { response?: { status?: number } } | undefined)
    ?.response?.status

  switch (status) {
    case 401:
      return 'link_invalid'
    case 410:
      return 'link_expired'
    case 403:
      return 'access_denied'
    default:
      return undefined
  }
}

export function accountingAccessErrorMessage(
  code: AccountingAccessErrorCode,
  providerName: string,
): string {
  switch (code) {
    case 'link_expired':
      return `This ${providerName} link has expired. Please reopen ${providerName} from the Lopay app.`
    case 'access_denied':
      return `You no longer have permission to manage ${providerName} for this merchant. Sign in to the Lopay app as an owner or admin and try again.`
    default:
      return `This ${providerName} link is no longer valid. Please reopen ${providerName} from the Lopay app.`
  }
}

export function accountingRequest<ReturnType>({
  method,
  url,
  credentials,
  data,
  params,
  headers,
}: {
  method: Method
  url: string
  credentials: AccountingAccessCredentials
  data?: unknown
  params?: Record<string, unknown>
  headers?: Record<string, string>
}): Promise<ReturnType> {
  return axiosInstance()
    .request<ReturnType>({
      method,
      url,
      data,
      ...requestConfigFor(credentials, params, headers),
    })
    .then((response) => response.data)
}
